import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowUpRight } from 'lucide-react';
import { hero, SITE } from '../data/mock';
import HeroMusic from './HeroMusic';
import HeroStats from './HeroStats';
import { useCopy } from '../context/SiteCopyContext';

const HeroVideo = () => {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const heroSubtitle = useCopy('home.hero.subtitle', 'Solid timber. Fine fabrics. Furniture built quietly in Australia — designed to sit in your home for decades, not seasons.');
  const ctaPrimary = useCopy('home.hero.cta_primary', 'Shop the collection');
  const ctaSecondary = useCopy('home.hero.cta_secondary', 'Our story');

  // Pause when out of view (perf)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="relative w-full h-[100vh] min-h-[640px] max-h-[1000px] overflow-hidden bg-[#1E140B]">
      <video
        ref={videoRef}
        src={hero.video}
        poster={hero.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
      {!loaded && (
        <img src={hero.fallback} alt="Timber & Thread Co. — Australian-made furniture" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1E140B]/30 via-transparent to-[#1E140B]/75" />

      {/* Right-edge vertical marquee — labels scroll upward continuously */}
      <div className="absolute right-5 lg:right-10 inset-y-0 hidden lg:block z-10 pointer-events-none w-[24px] overflow-hidden">
        <div className="hero-vertical-marquee absolute inset-x-0 top-0 flex flex-col items-end gap-12">
          {/* duplicate the list twice so the loop appears seamless */}
          {[0, 1].map((rep) => (
            <React.Fragment key={rep}>
              {['Solid timber', 'Heavyweight bouclé', 'Made in Melbourne', 'Designed in Australia', 'Built to outlast trends'].map((t, i) => (
                <span
                  key={`${rep}-${i}`}
                  style={{ writingMode: 'vertical-rl' }}
                  className="text-[10px] tracking-[0.5em] uppercase text-[#F5EDDD]/60 whitespace-nowrap"
                >
                  {t}
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative h-full max-w-[1440px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-24 text-[#F5EDDD] z-10">
        <div className="max-w-3xl">
          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-[92px] leading-[0.95] tracking-tight animate-[fadeUp_1100ms_ease-out_both]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="block">Where <span className="italic text-[#E8DBC4]">timber</span></span>
            <span className="block">meets <span className="italic text-[#E8DBC4]">thread</span>.</span>
          </h1>
          <p className="mt-7 max-w-xl text-[15px] sm:text-[16px] leading-[1.8] text-[#F5EDDD]/85 animate-[fadeUp_1100ms_ease-out_220ms_both]">
            {heroSubtitle}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4 animate-[fadeUp_1100ms_ease-out_420ms_both]">
            <Button asChild className="bg-[#F5EDDD] text-[#33200F] hover:bg-[#FBF6EC] rounded-none h-12 px-9 text-[12px] tracking-[0.22em] uppercase">
              <Link to="/collections">{ctaPrimary}</Link>
            </Button>
            <Button asChild variant="outline" className="bg-transparent border border-[#F5EDDD]/70 text-[#F5EDDD] hover:bg-[#F5EDDD] hover:text-[#33200F] rounded-none h-12 px-7 text-[12px] tracking-[0.22em] uppercase">
              <Link to="/about">{ctaSecondary} <ArrowUpRight className="ml-2 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="hidden lg:block">
          <HeroStats tone="dark" />
        </div>
      </div>

      {/* Ambient music control (renders if /media/hero-music.mp3 exists) */}
      <HeroMusic />
    </section>
  );
};

export default HeroVideo;
